/**
 * MigrateProjectDialog — 6-step "Migrate existing project" wizard in a right-side sheet.
 *
 *   1 Project basics   2 Integrations (each Validate-d)   3 SOW   4 Current phase   5 Team   6 Review
 *
 * Layout: the sheet is a full-height flex column — a fixed header (title, description, step
 * indicator, step heading, form error), a scrollable body holding only the step content, and a
 * pinned footer with Back / Cancel / Next. Long steps (Integrations, Team) scroll inside the body so
 * the actions never leave the viewport. The name stays `…Dialog`: it is still a Radix Dialog
 * (role="dialog", Esc closes, focus returns to the trigger) and ProjectsPage imports it by name.
 *
 * Mirrors CreateProjectStepper's structure (step indicator, per-step validation, Back/Next) and
 * posts POST /api/projects/migrate. Linking never creates anything: every filled-in integration must
 * pass its Validate check (the server re-checks on submit). On success the SOW file (if any) is PUT
 * to the presigned URL from the response, then the user lands on the new project.
 *
 * GitHub / Slack fields are read-only for roles other than admin / leadership (§12.1 — the server
 * returns 403 otherwise), exactly like the create wizard.
 */
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, X } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { canManageLinkage } from "@/lib/linkage";
import {
  EMPTY_MIGRATION_FORM,
  MIGRATION_STEPS,
  MIGRATION_TOOLS,
  LINKAGE_RESTRICTED_TOOLS,
  PHASE_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  STATUS_OPTIONS,
  TOOL_META,
  buildMigratePayload,
  mapMigrationError,
  stepForField,
  suppliedTools,
  validateBasics,
  validateIntegrations,
  validatePhase,
  validateSow,
  validateTeam,
  type FieldErrors,
  type MigrationForm,
  type MigrationTool,
  type ToolValidation,
} from "@/lib/migration";
import { isSharePointLink } from "@/lib/sharepoint-link";
import {
  useMigrateProject,
  useValidateMigrationIntegration,
} from "@/hooks/useProjectMigration";
import { projectRoleLabel } from "@/types/intake";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { FilePicker } from "@/components/ui/file-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { IntegrationField } from "./IntegrationField";
import { TeamStep } from "./TeamStep";

/** Mirrors MAX_UPLOAD_BYTES in packages/projects/services/upload-validation.service.ts. */
const MAX_SOW_BYTES = 104_857_600; // 100 MB
const SOW_ACCEPT = ".pdf,.docx,.doc,.txt,.md";

/** Server error field (`details.field`) → wizard form field. */
const SERVER_FIELD_MAP: Record<string, string> = {
  title: "title",
  client_name: "clientName",
  project_key: "projectKey",
  planned_kickoff_date: "kickoff",
  expected_completion_date: "completion",
  planned_ipr_date: "ipr",
  existing_srs_url: "srsUrl",
  existing_kickoff_deck_url: "kickoffDeckUrl",
  description: "description",
  "sow.url": "sowUrl",
  "sow.hours": "sowHours",
  "sow.hours_consumed": "hoursConsumed",
  "sow.file": "sowFile",
  current_phase: "currentPhase",
};

export interface MigrateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function FieldError({
  id,
  message,
}: {
  id: string;
  message?: string;
}): JSX.Element | null {
  if (!message) return null;
  return (
    <p id={id} role="alert" className="text-sm text-error">
      {message}
    </p>
  );
}

export function MigrateProjectDialog({
  open,
  onOpenChange,
}: MigrateProjectDialogProps): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canLinkRestricted = canManageLinkage(user?.role);
  const migrate = useMigrateProject();
  const validateIntegration = useValidateMigrationIntegration();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<MigrationForm>(EMPTY_MIGRATION_FORM);
  const [validations, setValidations] = useState<
    Partial<Record<MigrationTool, ToolValidation>>
  >({});
  const [sowFile, setSowFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const isLastStep = step === MIGRATION_STEPS.length - 1;

  // Move focus to the step heading so keyboard / screen-reader users land on the new content, and
  // start each step at the top of the scroll body (it would otherwise keep the previous offset).
  useEffect(() => {
    if (!open) return;
    headingRef.current?.focus();
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [step, open]);

  const set = <K extends keyof MigrationForm>(
    field: K,
    value: MigrationForm[K],
  ): void => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
    setFormError(null);
  };

  const setIntegration = (tool: MigrationTool, value: string): void => {
    setForm((f) => ({
      ...f,
      integrations: { ...f.integrations, [tool]: value },
    }));
    // Any edit invalidates the previous verdict.
    setValidations((v) => ({ ...v, [tool]: { status: "idle" } }));
    setErrors((e) => ({ ...e, [tool]: undefined }));
    setFormError(null);
  };

  const reset = (): void => {
    setStep(0);
    setForm(EMPTY_MIGRATION_FORM);
    setValidations({});
    setSowFile(null);
    setErrors({});
    setFormError(null);
    setSubmitting(false);
  };

  const handleClose = (next: boolean): void => {
    if (submitting) return;
    onOpenChange(next);
    if (!next) reset();
  };

  const runValidate = async (tool: MigrationTool): Promise<void> => {
    const value = form.integrations[tool].trim();
    if (!value) return;
    setValidations((v) => ({
      ...v,
      [tool]: { status: "checking", validatedValue: value },
    }));
    try {
      const result = await validateIntegration(tool, value);
      setValidations((v) =>
        // Ignore a stale answer if the user edited the field while the check was in flight.
        v[tool]?.validatedValue !== value
          ? v
          : {
              ...v,
              [tool]: {
                status: result.valid ? "valid" : "invalid",
                validatedValue: value,
                result,
              },
            },
      );
      setErrors((e) => ({ ...e, [tool]: undefined }));
    } catch (err) {
      setValidations((v) => ({
        ...v,
        [tool]: {
          status: "invalid",
          validatedValue: value,
          error: mapMigrationError(err, "validate").message,
        },
      }));
    }
  };

  const validateStep = (index: number): FieldErrors => {
    switch (index) {
      case 0:
        return validateBasics(form);
      case 1:
        return validateIntegrations(form, validations, canLinkRestricted);
      case 2: {
        const e = validateSow(form);
        if (sowFile && sowFile.size > MAX_SOW_BYTES) {
          e.sowFile = "The SOW file must be 100 MB or smaller.";
        }
        return e;
      }
      case 3:
        return validatePhase(form);
      case 4:
        return validateTeam(form.team);
      default:
        return {};
    }
  };

  const hasErrors = (e: FieldErrors): boolean => Object.values(e).some(Boolean);

  const handleNext = (): void => {
    const e = validateStep(step);
    setErrors(e);
    if (hasErrors(e)) {
      setFormError("Fix the highlighted fields to continue.");
      return;
    }
    setFormError(null);
    if (isLastStep) {
      void handleSubmit();
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = (): void => {
    setErrors({});
    setFormError(null);
    setStep(Math.max(step - 1, 0));
  };

  const uploadSow = async (
    upload: { upload_url: string; content_type: string },
    file: File,
  ): Promise<void> => {
    // content-type and content-length are signed into the URL (SEC-4); send the server's type.
    const res = await fetch(upload.upload_url, {
      method: "PUT",
      headers: { "Content-Type": upload.content_type },
      body: file,
    });
    if (!res.ok) throw new Error(`SOW upload failed (${res.status})`);
  };

  const handleSubmit = async (): Promise<void> => {
    // Re-check every step before posting (a step may have been edited after it was passed).
    for (let i = 0; i < MIGRATION_STEPS.length - 1; i++) {
      const e = validateStep(i);
      if (hasErrors(e)) {
        setErrors(e);
        setStep(i);
        setFormError("Fix the highlighted fields to continue.");
        return;
      }
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const payload = buildMigratePayload(
        form,
        validations,
        canLinkRestricted,
        sowFile,
      );
      const result = await migrate.mutateAsync(payload);
      const jiraKey = result.project.jira_key;

      if (sowFile && result.sow_upload) {
        try {
          await uploadSow(result.sow_upload, sowFile);
        } catch {
          toast.error(
            "Project migrated, but the SOW file didn't upload. You can add the SOW link from the project later.",
          );
        }
      }
      if (result.sow_download === "queued") {
        toast.success(
          "Downloading the SOW from SharePoint — it will appear on the project shortly.",
        );
      } else if (result.sow_download === "failed") {
        toast.error(
          "Project migrated, but the SOW couldn't be downloaded from SharePoint. Upload it from the project later.",
        );
      }
      if (result.propagation.attempted && !result.propagation.completed) {
        toast.warning(
          "Some team members may not have been added to the linked tools yet — use “Sync access” on the project.",
        );
      }
      toast.success(`${result.project.title} migrated to DeliverPro`);
      setSubmitting(false);
      onOpenChange(false);
      reset();
      navigate(`/projects/${encodeURIComponent(jiraKey)}`);
    } catch (err) {
      const mapped = mapMigrationError(err);
      setSubmitting(false);
      setFormError(mapped.message);
      if (mapped.field) {
        const target = stepForField(mapped.field);
        if (mapped.field.startsWith("integrations.")) {
          const tool = mapped.field.slice(
            "integrations.".length,
          ) as MigrationTool;
          const value = form.integrations[tool]?.trim() ?? "";
          setValidations((v) => ({
            ...v,
            [tool]: {
              status: "invalid",
              validatedValue: value,
              error: mapped.message,
            },
          }));
        } else {
          const formField = SERVER_FIELD_MAP[mapped.field] ?? mapped.field;
          setErrors({ [formField]: mapped.message });
        }
        setStep(target);
      }
    }
  };

  const phaseLabel = PHASE_OPTIONS.find(
    (p) => p.value === form.currentPhase,
  )?.label;
  const gatesToRecord = Number(form.currentPhase || 0);
  const linkedTools = suppliedTools(form, canLinkRestricted);

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      {/* Overrides the `right` variant's `w-3/4 sm:max-w-xl` (too narrow for two-column rows) and
          its `p-6 gap-4`: padding lives on the header / body / footer so the body can scroll
          edge-to-edge between a fixed header and a pinned footer. `min-h-0` on the body lets it
          shrink below its content height inside the flex column. */}
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl"
      >
        <div className="shrink-0 space-y-4 border-b border-outlines px-6 pb-4 pt-6">
          <SheetHeader>
            <SheetTitle>Migrate existing project</SheetTitle>
            <SheetDescription>
              Bring an active project into DeliverPro with its existing tools,
              SOW, phase and team. Nothing is created in GitHub, Slack, Jira,
              SharePoint or Clockify.
            </SheetDescription>
          </SheetHeader>

          <ol
            className="flex flex-wrap items-center gap-x-3 gap-y-2"
            aria-label="Migration steps"
          >
            {MIGRATION_STEPS.map((label, i) => {
              const completed = i < step;
              const active = i === step;
              return (
                <li key={label} className="flex items-center gap-2">
                  <span
                    className={cn(
                      "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold",
                      completed && "border-[#497d00] bg-[#497d00] text-white",
                      active && "border-violet-950 bg-white text-neutral-800",
                      !completed &&
                        !active &&
                        "border-neutral-200 bg-neutral-200 text-neutral-600",
                    )}
                    aria-current={active ? "step" : undefined}
                  >
                    {completed ? (
                      <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    ) : (
                      i + 1
                    )}
                    <span className="sr-only">
                      {completed ? " (completed)" : active ? " (current)" : ""}
                    </span>
                  </span>
                  <span
                    className={cn(
                      "whitespace-nowrap text-xs font-medium",
                      active ? "text-violet-950" : "text-neutral-600",
                    )}
                  >
                    {label}
                  </span>
                </li>
              );
            })}
          </ol>

          <h3
            ref={headingRef}
            tabIndex={-1}
            className="text-base font-semibold text-text-primary focus:outline-none"
          >
            Step {step + 1} of {MIGRATION_STEPS.length}: {MIGRATION_STEPS[step]}
          </h3>

          {/* In the fixed header, not the body, so the error is visible however far the user
              has scrolled when they press Next. */}
          {formError && (
            <Alert variant="destructive">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
        </div>

        <div
          ref={bodyRef}
          data-testid="migrate-sheet-body"
          className="min-h-0 flex-1 overflow-y-auto px-6 py-4"
        >
          <form
            className="space-y-4 py-1"
            noValidate
            onSubmit={(e: FormEvent<HTMLFormElement>) => {
              e.preventDefault();
              handleNext();
            }}
          >
            {/* ── Step 1: Project basics ── */}
            {step === 0 && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="mig-title">Project name *</Label>
                  <Input
                    id="mig-title"
                    value={form.title}
                    onChange={(e) => set("title", e.target.value)}
                    aria-required="true"
                    aria-invalid={errors.title ? true : undefined}
                    aria-describedby={
                      errors.title ? "mig-title-error" : undefined
                    }
                    placeholder="e.g. Acme Customer Portal"
                  />
                  <FieldError id="mig-title-error" message={errors.title} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="mig-client">Client / company *</Label>
                  <Input
                    id="mig-client"
                    value={form.clientName}
                    onChange={(e) => set("clientName", e.target.value)}
                    aria-required="true"
                    aria-invalid={errors.clientName ? true : undefined}
                    aria-describedby={
                      errors.clientName ? "mig-client-error" : undefined
                    }
                    placeholder="e.g. Acme Corp"
                  />
                  <FieldError
                    id="mig-client-error"
                    message={errors.clientName}
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="mig-type">Project type *</Label>
                    <Select
                      id="mig-type"
                      value={form.projectType}
                      onChange={(v) => set("projectType", v)}
                      options={PROJECT_TYPE_OPTIONS.map((o) => ({
                        value: o.value,
                        label: o.label,
                      }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="mig-status">Status *</Label>
                    <Select
                      id="mig-status"
                      value={form.status}
                      onChange={(v) =>
                        set("status", v as MigrationForm["status"])
                      }
                      options={STATUS_OPTIONS.map((o) => ({
                        value: o.value,
                        label: o.label,
                      }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="mig-kickoff">Planned kickoff</Label>
                    <DatePicker
                      id="mig-kickoff"
                      value={form.kickoff}
                      onChange={(v) => set("kickoff", v)}
                      error={errors.kickoff}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="mig-completion">Expected completion</Label>
                    <DatePicker
                      id="mig-completion"
                      value={form.completion}
                      onChange={(v) => set("completion", v)}
                      min={form.kickoff || undefined}
                      error={errors.completion}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="mig-ipr">Planned IPR</Label>
                    <DatePicker
                      id="mig-ipr"
                      value={form.ipr}
                      onChange={(v) => set("ipr", v)}
                      error={errors.ipr}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="mig-key">
                    Project ID{" "}
                    <span className="font-normal text-text-secondary">
                      (optional)
                    </span>
                  </Label>
                  <Input
                    id="mig-key"
                    value={form.projectKey}
                    onChange={(e) => set("projectKey", e.target.value)}
                    aria-invalid={errors.projectKey ? true : undefined}
                    aria-describedby={`mig-key-help${errors.projectKey ? " mig-key-error" : ""}`}
                    placeholder="e.g. ACME-01"
                  />
                  <p id="mig-key-help" className="text-xs text-text-secondary">
                    The project's ID in DeliverPro. Leave blank to generate one
                    (DP-###). It can't be changed later.
                  </p>
                  <FieldError id="mig-key-error" message={errors.projectKey} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="mig-description">Description</Label>
                  <Textarea
                    id="mig-description"
                    value={form.description}
                    onChange={(e) => set("description", e.target.value)}
                    maxLength={4000}
                    aria-invalid={errors.description ? true : undefined}
                    aria-describedby={
                      errors.description ? "mig-description-error" : undefined
                    }
                  />
                  <FieldError
                    id="mig-description-error"
                    message={errors.description}
                  />
                </div>
              </>
            )}

            {/* ── Step 2: Existing integrations ── */}
            {step === 1 && (
              <>
                <p className="text-sm text-text-secondary">
                  Link the tools this project already uses. Each one is checked
                  before it's linked — use Validate on every field you fill in.
                  Leave a field blank if the project doesn't use that tool; you
                  can link it later.
                </p>
                {MIGRATION_TOOLS.map((tool) => (
                  <IntegrationField
                    key={tool}
                    tool={tool}
                    value={form.integrations[tool]}
                    onChange={(v) => setIntegration(tool, v)}
                    onValidate={() => void runValidate(tool)}
                    validation={validations[tool]}
                    error={errors[tool]}
                    locked={
                      !canLinkRestricted && LINKAGE_RESTRICTED_TOOLS.has(tool)
                    }
                  />
                ))}
              </>
            )}

            {/* ── Step 3: SOW ── */}
            {step === 2 && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="mig-sow-url">SOW link</Label>
                  <Input
                    id="mig-sow-url"
                    value={form.sowUrl}
                    onChange={(e) => set("sowUrl", e.target.value)}
                    placeholder="https://<tenant>.sharepoint.com/…/SOW.docx"
                    aria-invalid={errors.sowUrl ? true : undefined}
                    aria-describedby={
                      errors.sowUrl
                        ? "mig-sow-url-help mig-sow-url-error"
                        : "mig-sow-url-help"
                    }
                  />
                  <p
                    id="mig-sow-url-help"
                    className="text-xs text-text-secondary"
                  >
                    {sowFile
                      ? "A file is attached, so the link is saved for reference only."
                      : isSharePointLink(form.sowUrl)
                        ? "This SharePoint document will be downloaded automatically after migration — no upload needed."
                        : "Paste a SharePoint link and the SOW is downloaded automatically, or upload the file below."}
                  </p>
                  <FieldError id="mig-sow-url-error" message={errors.sowUrl} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="mig-sow-file">SOW document</Label>
                  <FilePicker
                    id="mig-sow-file"
                    accept={SOW_ACCEPT}
                    files={sowFile ? [sowFile] : []}
                    onChange={(files) => {
                      setSowFile(files[0] ?? null);
                      setErrors((er) => ({ ...er, sowFile: undefined }));
                    }}
                    aria-invalid={errors.sowFile ? true : undefined}
                    aria-describedby={`mig-sow-file-help${errors.sowFile ? " mig-sow-file-error" : ""}`}
                  />
                  <p
                    id="mig-sow-file-help"
                    className="text-xs text-text-secondary"
                  >
                    PDF, Word, text or Markdown, up to 100 MB. Stored with the
                    project; it does not start Phase 0 AI generation.
                  </p>
                  <FieldError
                    id="mig-sow-file-error"
                    message={errors.sowFile}
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="mig-sow-hours">SOW hours</Label>
                    <Input
                      id="mig-sow-hours"
                      inputMode="decimal"
                      value={form.sowHours}
                      onChange={(e) => set("sowHours", e.target.value)}
                      placeholder="e.g. 480"
                      aria-invalid={errors.sowHours ? true : undefined}
                      aria-describedby={
                        errors.sowHours ? "mig-sow-hours-error" : undefined
                      }
                    />
                    <FieldError
                      id="mig-sow-hours-error"
                      message={errors.sowHours}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="mig-hours-used">
                      Hours consumed so far
                    </Label>
                    <Input
                      id="mig-hours-used"
                      inputMode="decimal"
                      value={form.hoursConsumed}
                      onChange={(e) => set("hoursConsumed", e.target.value)}
                      placeholder="e.g. 120"
                      aria-invalid={errors.hoursConsumed ? true : undefined}
                      aria-describedby={
                        errors.hoursConsumed
                          ? "mig-hours-used-error"
                          : undefined
                      }
                    />
                    <FieldError
                      id="mig-hours-used-error"
                      message={errors.hoursConsumed}
                    />
                  </div>
                </div>
                {/* T-48/49: documents the project already has (linked, not re-generated). */}
                <fieldset className="space-y-4">
                  <legend className="text-sm font-medium text-text-primary">
                    Existing documents
                  </legend>
                  <p className="text-xs text-text-secondary">
                    Link the SRS and kickoff deck this project already has
                    (SharePoint, Google Docs or any https link). Optional.
                  </p>
                  <div className="space-y-1.5">
                    <Label htmlFor="mig-srs-url">Existing SRS link</Label>
                    <Input
                      id="mig-srs-url"
                      value={form.srsUrl}
                      onChange={(e) => set("srsUrl", e.target.value)}
                      placeholder="https://<tenant>.sharepoint.com/…/SRS.docx"
                      aria-invalid={errors.srsUrl ? true : undefined}
                      aria-describedby={
                        errors.srsUrl ? "mig-srs-url-error" : undefined
                      }
                    />
                    <FieldError
                      id="mig-srs-url-error"
                      message={errors.srsUrl}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="mig-deck-url">
                      Existing kickoff deck link
                    </Label>
                    <Input
                      id="mig-deck-url"
                      value={form.kickoffDeckUrl}
                      onChange={(e) => set("kickoffDeckUrl", e.target.value)}
                      placeholder="https://docs.google.com/presentation/d/…"
                      aria-invalid={errors.kickoffDeckUrl ? true : undefined}
                      aria-describedby={
                        errors.kickoffDeckUrl ? "mig-deck-url-error" : undefined
                      }
                    />
                    <FieldError
                      id="mig-deck-url-error"
                      message={errors.kickoffDeckUrl}
                    />
                  </div>
                </fieldset>
              </>
            )}

            {/* ── Step 4: Current phase ── */}
            {step === 3 && (
              <fieldset
                className="space-y-2"
                aria-describedby={`mig-phase-help${errors.currentPhase ? " mig-phase-error" : ""}`}
              >
                <legend className="text-sm font-medium text-text-primary">
                  Which CASDM phase is the project in now? *
                </legend>
                <p id="mig-phase-help" className="text-xs text-text-secondary">
                  Every gate before this phase is recorded as passed (marked
                  “migrated”), so the project opens in the right phase. Gate
                  checklists for earlier phases stay unchecked.
                </p>
                {PHASE_OPTIONS.map((p) => (
                  <label
                    key={p.value}
                    className={cn(
                      "flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm",
                      form.currentPhase === p.value
                        ? "border-primary bg-primary/5"
                        : "border-outlines",
                    )}
                  >
                    <input
                      type="radio"
                      name="mig-phase"
                      value={p.value}
                      checked={form.currentPhase === p.value}
                      onChange={() => set("currentPhase", p.value)}
                      className="h-4 w-4 accent-[var(--color-primary)]"
                    />
                    {p.label}
                  </label>
                ))}
                <FieldError
                  id="mig-phase-error"
                  message={errors.currentPhase}
                />
              </fieldset>
            )}

            {/* ── Step 5: Team ── */}
            {step === 4 && (
              <TeamStep
                team={form.team}
                onChange={(team) => {
                  setForm((f) => ({ ...f, team }));
                  setErrors({});
                }}
                errors={errors}
              />
            )}

            {/* ── Step 6: Review ── */}
            {step === 5 && (
              <div className="space-y-4 text-sm">
                <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2">
                  <dt className="text-text-secondary">Project</dt>
                  <dd className="text-text-primary">
                    {form.title} — {form.clientName}
                  </dd>
                  <dt className="text-text-secondary">Type / status</dt>
                  <dd>
                    {PROJECT_TYPE_OPTIONS.find(
                      (o) => o.value === form.projectType,
                    )?.label ?? form.projectType}{" "}
                    · {form.status}
                  </dd>
                  <dt className="text-text-secondary">Project ID</dt>
                  <dd>
                    {form.projectKey.trim().toUpperCase() ||
                      "Generated (DP-###)"}
                  </dd>
                  <dt className="text-text-secondary">Dates</dt>
                  <dd>
                    {form.kickoff || "—"} → {form.completion || "—"}
                    {form.ipr ? ` · IPR ${form.ipr}` : ""}
                  </dd>
                  <dt className="text-text-secondary">Existing documents</dt>
                  <dd>
                    {[
                      form.srsUrl.trim() && "SRS link",
                      form.kickoffDeckUrl.trim() && "kickoff deck link",
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </dd>
                  <dt className="text-text-secondary">Integrations</dt>
                  <dd>
                    {linkedTools.length === 0 ? (
                      "None — link tools later from the project page"
                    ) : (
                      <ul className="space-y-0.5">
                        {linkedTools.map((t) => (
                          <li key={t}>
                            {TOOL_META[t].label}:{" "}
                            {validations[t]?.result?.display ??
                              form.integrations[t]}
                          </li>
                        ))}
                      </ul>
                    )}
                  </dd>
                  <dt className="text-text-secondary">SOW</dt>
                  <dd>
                    {[
                      form.sowUrl.trim() && "link",
                      sowFile?.name,
                      form.sowHours.trim() && `${form.sowHours} h`,
                      form.hoursConsumed.trim() &&
                        `${form.hoursConsumed} h consumed`,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </dd>
                  <dt className="text-text-secondary">Current phase</dt>
                  <dd>{phaseLabel ?? "—"}</dd>
                  <dt className="text-text-secondary">Team</dt>
                  <dd>
                    {form.team.length === 0 ? (
                      "—"
                    ) : (
                      <ul className="space-y-0.5">
                        {form.team.map((r) => (
                          <li key={r.key}>
                            {r.resourceName || "—"} ({projectRoleLabel(r.role)})
                          </li>
                        ))}
                      </ul>
                    )}
                  </dd>
                </dl>
                <p className="rounded-md bg-subtle-bg p-3 text-text-secondary">
                  {gatesToRecord === 0
                    ? "No gates will be recorded — the project starts in Phase 0. "
                    : gatesToRecord === 1
                      ? "Gate 0 will be recorded as passed (migrated). "
                      : `Gates 0–${gatesToRecord - 1} will be recorded as passed (migrated). `}
                  Tools you didn't link are marked “skipped” and are not
                  auto-created.
                </p>
              </div>
            )}
          </form>
        </div>

        <SheetFooter
          data-testid="migrate-sheet-footer"
          className="shrink-0 flex-row justify-between gap-2 border-t border-outlines bg-white px-6 py-4 sm:justify-between sm:space-x-0"
        >
          <div>
            {step > 0 && (
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={submitting}
              >
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={submitting}
            >
              <X className="h-4 w-4" aria-hidden="true" />
              Cancel
            </Button>
            <Button onClick={handleNext} disabled={submitting}>
              {submitting && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {isLastStep
                ? submitting
                  ? "Migrating…"
                  : "Migrate project"
                : "Next"}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default MigrateProjectDialog;
